import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  OAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../firebase';
import symfonyApiService from './symfonyApiService';

class AuthService {
  // Connexion avec email et mot de passe
  async signIn(email: string, password: string): Promise<FirebaseUser> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ Connexion réussie:', userCredential.user.email);
      return userCredential.user;
    } catch (error: any) {
      console.error('❌ Erreur de connexion:', error);
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  // Inscription avec email et mot de passe
  async signUp(email: string, password: string): Promise<FirebaseUser> {
    try {
      // 1. Créer l'utilisateur dans Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('✅ Inscription Firebase réussie:', userCredential.user.email);
      
      // 2. Créer l'utilisateur dans Symfony
      try {
        await symfonyApiService.createCompleteUser(
          {
            email: userCredential.user.email!,
            logintype: 'email'
          },
          userCredential.user.uid
        );
      } catch (symfonyError) {
        console.error('⚠️ Erreur Symfony (utilisateur Firebase créé):', symfonyError);
        // On ne bloque pas l'inscription même si Symfony échoue
      }
      
      return userCredential.user;
    } catch (error: any) {
      console.error('❌ Erreur d\'inscription:', error);
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  // Connexion avec Apple (avec popup)
  async signInWithApple(): Promise<FirebaseUser> {
    try {
      const provider = new OAuthProvider('apple.com');
      
      // Configuration des scopes Apple
      provider.addScope('email');
      provider.addScope('name');
      
      // Paramètres personnalisés pour Apple
      provider.setCustomParameters({
        'locale': 'fr'
      });

      console.log('🍎 Ouverture popup Apple...');
      const result = await signInWithPopup(auth, provider);
      console.log('✅ Connexion Apple réussie:', result.user.email);
      
      // Créer l'utilisateur dans Symfony si nécessaire
      try {
        await symfonyApiService.createCompleteUser(
          {
            email: result.user.email!,
            logintype: 'apple'
          },
          result.user.uid
        );
      } catch (symfonyError) {
        console.error('⚠️ Erreur Symfony (utilisateur Apple existant ou erreur):', symfonyError);
        // On ne bloque pas la connexion même si Symfony échoue
      }
      
      return result.user;
    } catch (error: any) {
      console.error('❌ Erreur de connexion Apple:', error);
      console.error('Code erreur:', error.code);
      console.error('Message:', error.message);
      
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  // Déconnexion
  async signOut(): Promise<void> {
    try {
      await signOut(auth);
      console.log('✅ Déconnexion réussie');
    } catch (error) {
      console.error('❌ Erreur de déconnexion:', error);
      throw error;
    }
  }

  // Réinitialisation du mot de passe
  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
      console.log('✅ Email de réinitialisation envoyé à:', email);
    } catch (error: any) {
      console.error('❌ Erreur de réinitialisation:', error);
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  // Observer les changements d'état d'authentification
  onAuthStateChanged(callback: (user: FirebaseUser | null) => void) {
    return onAuthStateChanged(auth, callback);
  }

  // Obtenir l'utilisateur actuel
  getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }

  // Vérifier si l'utilisateur est connecté
  isAuthenticated(): boolean {
    return !!auth.currentUser;
  }

  // Traduire les codes d'erreur Firebase
  private getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/user-not-found':
        return '❌ Aucun compte trouvé avec cette adresse email.';
      case 'auth/wrong-password':
        return '❌ Mot de passe incorrect. Veuillez réessayer.';
      case 'auth/email-already-in-use':
        return '❌ Cette adresse email est déjà utilisée par un autre compte.';
      case 'auth/weak-password':
        return '❌ Le mot de passe doit contenir au moins 6 caractères.';
      case 'auth/invalid-email':
        return '❌ Adresse email invalide. Vérifiez le format.';
      case 'auth/invalid-credential':
        return '❌ Identifiants invalides. Vérifiez votre email et mot de passe.';
      case 'auth/too-many-requests':
        return '❌ Trop de tentatives. Veuillez réessayer dans quelques minutes.';
      case 'auth/network-request-failed':
        return '❌ Erreur de connexion. Vérifiez votre connexion internet.';
      case 'auth/popup-closed-by-user':
        return '❌ Connexion annulée. Veuillez réessayer.';
      case 'auth/popup-blocked':
        return '❌ Pop-up bloquée. Autorisez les pop-ups pour ce site dans vos paramètres.';
      case 'auth/cancelled-popup-request':
        return '❌ Demande de connexion annulée.';
      case 'auth/account-exists-with-different-credential':
        return '❌ Un compte existe déjà avec cette adresse email mais avec un autre mode de connexion.';
      case 'auth/user-disabled':
        return '❌ Ce compte a été désactivé. Contactez le support.';
      case 'auth/operation-not-allowed':
        return '❌ Cette méthode de connexion n\'est pas autorisée.';
      case 'auth/requires-recent-login':
        return '❌ Cette action nécessite une reconnexion récente. Veuillez vous reconnecter.';
      case 'auth/unauthorized-domain':
        return '❌ Ce domaine n\'est pas autorisé pour l\'authentification. Contactez le support.';
      case 'auth/operation-not-supported-in-this-environment':
        return '❌ Cette opération n\'est pas supportée dans cet environnement. Utilisez HTTPS.';
      default:
        return '❌ Une erreur est survenue. Veuillez réessayer.';
    }
  }
}

const authService = new AuthService();
export default authService;